import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Users, Clock, ChevronRight, MoreHorizontal, Plus, X } from 'lucide-react';
const projects = [{
  id: 1,
  name: 'The Oakwood',
  status: 'Active',
  buyers: 124,
  lastActivity: '2 mins ago',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80'
}, {
  id: 2,
  name: 'The Maple Residences',
  status: 'Draft',
  buyers: 0,
  lastActivity: '2 days ago',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600&q=80'
}];
export function ProjectsPage() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: ''
  });
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCreateModal) {
        setShowCreateModal(false);
        setNewProject({
          name: '',
          description: ''
        });
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showCreateModal]);
  const handleCreateProject = () => {
    console.log('Creating project:', newProject);
    // In production, this would create the project
    setShowCreateModal(false);
    setNewProject({
      name: '',
      description: ''
    });
  };
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-2083">
        <div className="flex items-center justify-between" data-id="element-2084">
          <div data-id="element-2085">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-2086">Projects</h1>
            <p className="text-slate-500" data-id="element-2087">
              Manage your active developments and models.
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} data-id="element-2088">New Project</Button>
        </div>

        <div className="grid grid-cols-1 gap-6" data-id="element-2089">
          {projects.map(project => <Card key={project.id} className="overflow-hidden hover:shadow-md transition-shadow" data-id="element-2090">
              <div className="flex flex-col md:flex-row" data-id="element-2091">
                {/* Image Section */}
                <div className="w-full md:w-64 h-48 md:h-auto bg-slate-100 relative overflow-hidden" data-id="element-2092">
                  <img src={project.image} alt={project.name} className="w-full h-full object-cover" data-id="element-2093" />
                  <div className="absolute top-3 left-3" data-id="element-2094">
                    <Badge variant={project.status === 'Active' ? 'success' : 'neutral'} data-id="element-2095">
                      {project.status}
                    </Badge>
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 p-6 flex flex-col justify-between" data-id="element-2096">
                  <div data-id="element-2097">
                    <div className="flex items-start justify-between mb-2" data-id="element-2098">
                      <h3 className="text-xl font-bold text-slate-900" data-id="element-2099">
                        {project.name}
                      </h3>
                      <button className="text-slate-400 hover:text-slate-600" data-id="element-2100">
                        <MoreHorizontal className="h-5 w-5" data-id="element-2101" />
                      </button>
                    </div>
                    <p className="text-slate-500 mb-6 max-w-2xl" data-id="element-2102">
                      Single family homes collection featuring modern amenities
                      and sustainable design.
                    </p>

                    <div className="flex items-center gap-6 text-sm text-slate-600" data-id="element-2103">
                      <div className="flex items-center gap-2" data-id="element-2104">
                        <Users className="h-4 w-4 text-slate-400" data-id="element-2105" />
                        <span data-id="element-2106">{project.buyers} Active Buyers</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-2107">
                        <Clock className="h-4 w-4 text-slate-400" data-id="element-2108" />
                        <span data-id="element-2109">Updated {project.lastActivity}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-100 flex items-center gap-4" data-id="element-2110">
                    <Button onClick={() => navigate('/options')} leftIcon={<FolderOpen className="h-4 w-4" data-id="element-2112" />} data-id="element-2111">
                      Manage Options
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/analytics')} data-id="element-2113">
                      View Analytics
                    </Button>
                  </div>
                </div>
              </div>
            </Card>)}
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2114">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" data-id="element-2115">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between" data-id="element-2116">
              <div data-id="element-2117">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-2118">
                  Create New Project
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-2119">
                  Add a new development project to your portfolio
                </p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-2120">
                <X className="h-5 w-5" data-id="element-2121" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-2122">
              <div data-id="element-2123">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2124">
                  Project Name
                </label>
                <input type="text" value={newProject.name} onChange={e => setNewProject({
              ...newProject,
              name: e.target.value
            })} placeholder="e.g., The Riverside Collection" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-2125" />
              </div>
              <div data-id="element-2126">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2127">
                  Description (Optional)
                </label>
                <textarea value={newProject.description} onChange={e => setNewProject({
              ...newProject,
              description: e.target.value
            })} placeholder="Brief description of the project..." rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-2128" />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3" data-id="element-2129">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} data-id="element-2130">
                Cancel
              </Button>
              <Button onClick={handleCreateProject} disabled={!newProject.name.trim()} leftIcon={<Plus className="h-4 w-4" data-id="element-2132" />} data-id="element-2131">
                Create Project
              </Button>
            </div>
          </div>
        </div>}
    </>;
}